<script setup lang="ts">
import { computed, ref } from 'vue'
import { sources } from '@bc-library/recruitment'
import { useInfinityCompany } from '@/services/company'
import { useInfinityResource } from '@/services/resource'
import { useInfinityRecruitmentWebsite } from '@/services/recruitmentWebsite'
import { useInfinitySchool } from '@/services/school'
import { useInfinityJobOffer } from '@/services/jobOffer'
import Combobox from '@/components/common/Combobox.vue'
import BRadios from '@/components/common/BRadios.vue'
import JobOfferComboItem from '@/components/recruitment/JobOfferComboItem.vue'
import ListItemResource from '@/components/resource/autocomplete/ListItem.vue'
import TextInput from '@/components/common/textInput.vue'
import bSelect from '@/components/common/bSelect.vue'
import { size as sizeConst } from '@/services/application/constantes'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()

const props = withDefaults(defineProps<{
  size?: keyof typeof sizeConst
  required?: boolean
  dataCy?: string
  /** Asks whether an application sent online was spontaneous or for an offer (the creation form has its own offer field) */
  withOffer?: boolean
}>(), { size: 'small', required: false, dataCy: undefined, withOffer: false })

const recruitmentProcess = defineModel<{
  candidateSource: number | null
  candidateSourcePopulated: {
    _id: number
    display: string
  }
  candidate: {
    degrees: {
      _id: string
      tempId: string
      schoolId: string
      school: null | {
        _id: string
        display: string
      }
      categoryId: string
      category: null | {
        _id: string
        display: string
      }
      startYear: number
      endYear: number
      description: string
    }[]
  }
  recommendedBy: {
    resource: {
      _id: string
      firstname: string
      lastname: string
      picture?: string
      display?: string
    } | null
    resourceId: string | null
    company: {
      _id: string
      display: string
    } | null
    companyId: string | null
    degree: {
      _id: string
      display: string
    } | null
    freeText: string
    website: {
      _id: string
      display: string
    } | null
    websiteId: string | null
    websitePopulated: {
      _id: string
      display: string
    } | null
    offerId?: string | null
    offer?: {
      _id: string
      title?: string
    } | null
  }
  externalApplication?: { provider?: string } | null
}>()

// Sorted by their name in the user's language
const sourceOptions = computed(() => sources
  .map(source => ({ ...source, display: t(source.display) }))
  .sort((a, b) => a.display.localeCompare(b.display, locale.value)))

const sourceLinked = computed({
  get: () => recruitmentProcess.value?.candidateSourcePopulated,
  set: (value) => {
    if (!recruitmentProcess.value) return
    recruitmentProcess.value.candidateSourcePopulated = value
    recruitmentProcess.value.candidateSource = value?._id === 0 ? value?._id : value?._id || null
  },
})

const websiteSelected = computed({
  get: () => recruitmentProcess.value?.recommendedBy.websitePopulated || null,
  set: (value) => {
    if (!recruitmentProcess.value) return
    recruitmentProcess.value.recommendedBy.websitePopulated = value || null
    recruitmentProcess.value.recommendedBy.websiteId = value?._id || null
  },
})

const companyRecommendedBy = computed({
  get: () => recruitmentProcess.value?.recommendedBy.company || null,
  set: (value) => {
    if (!recruitmentProcess.value) return
    recruitmentProcess.value.recommendedBy.company = value || null
    recruitmentProcess.value.recommendedBy.companyId = value?._id || null
  },
})

const resourceRecommendedBy = computed({
  get: () => {
    const resource = recruitmentProcess.value?.recommendedBy.resource
    if (!resource) return null
    return {
      ...resource,
      display: `${resource.firstname} ${resource.lastname}`,
    }
  },
  set: (value) => {
    if (!recruitmentProcess.value) return
    recruitmentProcess.value.recommendedBy.resource = value ? {
      _id: value._id,
      firstname: value.firstname,
      lastname: value.lastname,
      picture: value.picture,
    } : null
    recruitmentProcess.value.recommendedBy.resourceId = value?._id || null
  },
})

const degreeSelected = computed({
  get: () => recruitmentProcess.value?.recommendedBy.degree || null,
  set: (value) => {
    if (!recruitmentProcess.value) return
    recruitmentProcess.value.recommendedBy.degree = value || null
  },
})


// Applications sent online (a website, a job board, the career site): spontaneous, or for one of the offers
const ONLINE_SOURCES = [1, 8, 99]
const isOnlineApplication = computed(() => props.withOffer && ONLINE_SOURCES.includes(sourceLinked.value?._id))
// A job board reads back the offer of the application it pushed
const isOfferLocked = computed(() => Boolean(recruitmentProcess.value?.externalApplication?.provider))

const offerSelected = computed({
  get: () => recruitmentProcess.value?.recommendedBy?.offer || null,
  set: (value) => {
    if (!recruitmentProcess.value) return
    // Old processes may have no recommendedBy at all
    recruitmentProcess.value.recommendedBy = { ...recruitmentProcess.value.recommendedBy, offerId: value?._id || null, offer: value || null }
  },
})

// Spontaneous until an offer is chosen: the career site saves spontaneous applications without an offer too
const appliedToOffer = ref(Boolean(recruitmentProcess.value?.recommendedBy?.offerId))
const applicationKind = computed({
  get: () => appliedToOffer.value ? 'offer' : 'spontaneous',
  set: (value) => {
    appliedToOffer.value = value === 'offer'
    if (!appliedToOffer.value) offerSelected.value = null
  },
})

const { getMoreData: getMoreWebsite, haveMore: haveMoreWebsite, limit: limitWebsite, page: pageWebsite, list: websiteList, search: searchWebsite, isLoading: isLoadingWebsite } = useInfinityRecruitmentWebsite({})
const { getMoreData: getMoreCompany, haveMore: haveMoreCompany, limit: limitCompany, page: pageCompany, list: companyList, search: searchCompany, isLoading: isLoadingCompany } = useInfinityCompany({})
const { getMoreData: getMoreResource, haveMore: haveMoreResource, limit: limitResource, page: pageResource, list: resourceList, search: searchResource, isLoading: isLoadingResource } = useInfinityResource({})
const { getMoreData: getMoreSchool, haveMore: haveMoreSchool, limit: limitSchool, page: pageSchool, list: schoolList, search: searchSchool, isLoading: isLoadingSchool } = useInfinitySchool({ })
// Expired offers still have candidates in progress
const { getMoreData: getMoreOffer, haveMore: haveMoreOffer, limit: limitOffer, page: pageOffer, list: offerList, search: searchOffer, isLoading: isLoadingOffer } = useInfinityJobOffer({ status: 'all', order: 'activeFirst' })
</script>

<template>
  <div class="flex flex-col mt-2 gap-2" :data-cy="props.dataCy">
    <bSelect
      v-model="sourceLinked"
      class="rounded-lg"
      :options="sourceOptions"
      :label="t('common.applicationSource')"
      :size="size"
      :required="required"
    />

    <div v-auto-animate>
      <!-- Website (sourceLinked._id === 1) -->
      <Combobox
        v-if="sourceLinked?._id === 1"
        v-model="websiteSelected"
        v-model:search="searchWebsite"
        v-model:limit="limitWebsite"
        v-model:page="pageWebsite"
        v-model:have-more="haveMoreWebsite"
        :loading="isLoadingWebsite"
        :options="websiteList"
        :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.website')"
        class="w-full"
        @search="getMoreWebsite"
      />

      <!-- Company (sourceLinked._id === 2) -->
      <Combobox
        v-else-if="sourceLinked?._id === 2"
        v-model="companyRecommendedBy"
        v-model:search="searchCompany"
        v-model:limit="limitCompany"
        v-model:page="pageCompany"
        v-model:have-more="haveMoreCompany"
        :loading="isLoadingCompany"
        :options="companyList"
        :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.company')"
        class="w-full"
        @search="getMoreCompany"
      />

      <!-- Employee Referral (sourceLinked._id === 3) -->
      <Combobox
        v-else-if="sourceLinked?._id === 3"
        v-model="resourceRecommendedBy"
        v-model:search="searchResource"
        v-model:limit="limitResource"
        v-model:page="pageResource"
        v-model:have-more="haveMoreResource"
        :loading="isLoadingResource"
        :options="resourceList"
        :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.recommended')"
        class="w-full"
        @search="getMoreResource"
      >
        <template #custom-item="{ item }">
          <ListItemResource :resource="item" />
        </template>
        <template #selected-render="{ selected }">
          <ListItemResource :resource="selected" />
        </template>
      </Combobox>

      <!-- Alumni/Client Referral or Recruitment Event (sourceLinked._id === 4 or 6) -->
      <TextInput
        v-else-if="sourceLinked?._id === 4 || sourceLinked?._id === 6"
        v-model="recruitmentProcess.recommendedBy.freeText"
        container-class="w-full"
        :label="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.sourcePlaceholder')"
        label-fixed
      />

      <!-- School Forum (sourceLinked._id === 5) -->
      <Combobox
        v-else-if="sourceLinked?._id === 5"
        v-model="degreeSelected"
        v-model:search="searchSchool"
        v-model:limit="limitSchool"
        v-model:page="pageSchool"
        v-model:have-more="haveMoreSchool"
        :loading="isLoadingSchool"
        :options="schoolList"
        :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.degrees')"
        class="w-full"
        @search="getMoreSchool"
      />

      <!-- Internal Headhunting (sourceLinked._id === 7) -->
      <Combobox
        v-else-if="sourceLinked?._id === 7"
        v-model="resourceRecommendedBy"
        v-model:search="searchResource"
        v-model:limit="limitResource"
        v-model:page="pageResource"
        v-model:have-more="haveMoreResource"
        :loading="isLoadingResource"
        :options="resourceList"
        :placeholder="t('modules.recruitment.screening.editCandidateInformations.recruitmentProcess.selectCandidateSource.sourceBy')"
        class="w-full"
        @search="getMoreResource"
      >
        <template #custom-item="{ item }">
          <ListItemResource :resource="item" />
        </template>
        <template #selected-render="{ selected }">
          <ListItemResource :resource="selected" />
        </template>
      </Combobox>
    </div>

    <div v-if="isOnlineApplication" v-auto-animate class="flex flex-col gap-2" data-cy="candidate-source-application-kind">
      <BRadios
        v-model:selected-option="applicationKind"
        :options="[
          { value: 'spontaneous', label: t('components.recruitment.processOffer.spontaneous') },
          { value: 'offer', label: t('components.recruitment.processOffer.appliedToOffer') },
        ]"
        :disabled="isOfferLocked"
      />
      <Combobox
        v-if="applicationKind === 'offer'"
        v-model="offerSelected"
        v-model:search="searchOffer"
        v-model:limit="limitOffer"
        v-model:page="pageOffer"
        v-model:have-more="haveMoreOffer"
        class="w-full"
        :loading="isLoadingOffer"
        :disabled="isOfferLocked"
        :placeholder="t('components.recruitment.processOffer.placeholder')"
        :options="offerList"
        data-cy="candidate-source-offer-combobox"
        @search="getMoreOffer"
      >
        <template #selected-render="{ selected }">
          <span class="truncate">{{ selected?.title }}</span>
        </template>
        <template #custom-item="{ item }">
          <JobOfferComboItem :offer="item" />
        </template>
      </Combobox>
      <p v-if="isOfferLocked" class="subtitle text-xs">
        {{ t('components.recruitment.processOffer.lockedByJobBoard') }}
      </p>
      <p v-else-if="applicationKind === 'offer'" class="subtitle text-xs">
        {{ t('components.recruitment.processOffer.hint') }}
      </p>
    </div>
  </div>
</template>
